<template>
  <picture v-if="isGif" class="responsive-image">
    <img
      width="380"
      height="285"
      :alt="alt"
      :title="title"
      :loading="noLazyLoading ? 'eager' : 'lazy'"
      :src="url"
      :class="imageClass"
      :draggable="noDraggable ? 'false' : undefined"
    />
  </picture>
  <picture v-else class="responsive-image">
    <source
      v-for="source in sources"
      :key="source?.media"
      :media="source?.media"
      type="image/jpeg"
      :srcset="source?.srcset.join(', ')"
    />

    <img
      v-if="fallback"
      width="380"
      height="285"
      :alt="alt"
      :loading="noLazyLoading ? 'eager' : 'lazy'"
      :src="fallback"
      :class="imageClass"
      :draggable="noDraggable ? 'false' : undefined"
    />
  </picture>
</template>

<script lang="ts" setup>
import type { ResponsiveStyleName } from '~/helpers/responsiveStyles'
import {
  responsiveStylesMap,
  BREAKPOINTS,
  breakpointOrder,
  getImageStyleUrl,
  getBaseUrl,
} from '~/helpers/responsiveStyles'
import type { ClassProp } from '~/types/vue'

interface Source {
  media?: string
  srcset: string[]
  breakpoint: string
}

const props = withDefaults(
  defineProps<{
    url?: string
    alt?: string
    title?: string
    hash?: string
    responsiveStyle?: ResponsiveStyleName
    noLazyLoading?: boolean
    imageClass?: ClassProp
    noDraggable?: boolean
  }>(),
  {
    url: '',
    alt: '',
    title: '',
    hash: '',
    responsiveStyle: 'column_full',
    noLazyLoading: false,
    imageClass: '',
    noDraggable: false,
  },
)

const baseUrl = computed(() => getBaseUrl(props.url) || '')

const responsiveStyleItem = computed(() =>
  responsiveStylesMap.get(props.responsiveStyle),
)

const fallback = computed(() => {
  if (!baseUrl.value) {
    return undefined
  }
  const style =
    responsiveStyleItem.value?.styles?.[0]?.id || '300_225_focal_scale_crop'
  return getImageStyleUrl(style, baseUrl.value, props.hash)
})

const isGif = computed(() => {
  return props.url.endsWith('.gif')
})

const sources = computed(() => {
  if (!baseUrl.value) {
    return []
  }
  const sources: Record<string, Source> = {}
  responsiveStyleItem.value?.styles.forEach((style) => {
    if (!sources[style.viewport]) {
      sources[style.viewport] = {
        media: BREAKPOINTS[style.viewport],
        srcset: [],
        breakpoint: style.viewport,
      }
    }
    sources[style.viewport]?.srcset.push(
      `${getImageStyleUrl(style.id, baseUrl.value, props.hash)} ${
        style.density
      }`,
    )
  })
  return Object.values(sources).sort(
    (a, b) =>
      breakpointOrder.indexOf(a.breakpoint) -
      breakpointOrder.indexOf(b.breakpoint),
  )
})
</script>

<style>
@reference '~/assets/css/tailwind.css';
.responsive-image {
  @apply block;
  img {
    @apply block w-full;
  }
}
</style>
